import type MessageList from '@/components/chat/MessageList.vue'
import type ChatComposer from '@/components/chat/ChatComposer.vue'
import type ConversationSettingsMenu from '@/features/story-conversation/components/ConversationSettingsMenu.vue'
import type { DirectorNoteDraft } from '@/features/story-conversation/directorNotes'
import type { ChatComposerState } from '@/features/story-conversation/composerState'

import type { ChatMessageFeatures } from '@/components/chat/types'
import type { MessageSlot } from '@/lib/messageSlots'

export interface StoryMessageSave {
  busy: boolean
  description?: string
  retry: () => StorySaveFeedback | null | Promise<StorySaveFeedback | null>
}

export interface StorySaveFeedback {
  message: string
  variant: 'success' | 'error' | 'info'
}

type MessageId = string | number
type CommandResult = unknown | Promise<unknown>

export type StoryTimeline = Pick<InstanceType<typeof MessageList>['$props'],
  'slots' | 'character' | 'persona' | 'timelineMessages' | 'mostRecentMessageId' |
  'features' | 'featuresForSlot' | 'thinkingLabelForSlot' | 'customEntryForSlot' |
  'loadingOlder' | 'hasMore' | 'loadOlder' | 'alternatives' | 'forkDisabled' | 'forkingMessageId'>
export type StoryComposer = Pick<InstanceType<typeof ChatComposer>['$props'],
  'state' | 'canSend' | 'disabled' | 'inputReadonly' | 'canRewrite' | 'rewriteMode' |
  'rewriteBotName' | 'messagePlaceholder' | 'notePlaceholder' | 'showTools' | 'directorNotes'>
export type StoryMenu = Pick<InstanceType<typeof ConversationSettingsMenu>['$props'],
  'bot' | 'selectBot' | 'saveGenerationSettings' | 'disabled' | 'sections'>

/** Surface commands retain their product persistence and error semantics. */
export interface StoryConversationCommands {
  updateComposer: (state: ChatComposerState) => void
  send: () => CommandResult
  stop: () => CommandResult
  rewrite?: () => CommandResult
  restoreOriginal?: () => CommandResult
  edit?: (id: MessageId, content: string) => CommandResult
  delete?: (id: MessageId) => CommandResult
  fork?: (id: MessageId) => CommandResult
  reroll?: (id: MessageId) => CommandResult
  resend?: (id: MessageId) => CommandResult
  selectVariant?: (id: MessageId, variantId: MessageId) => CommandResult
  updateDirectorNote?: (id: MessageId, note: DirectorNoteDraft) => CommandResult
  deleteDirectorNote?: (id: MessageId) => CommandResult
}

/** One complete story-play presentation, independent of any persistence schema. */
export interface StoryConversationPresentation {
  key: string
  active: boolean
  streaming: boolean
  readOnly?: boolean
  accentName?: string | null
  timeline: StoryTimeline
  saveForSlot?: (slot: MessageSlot) => StoryMessageSave | null
  composer: StoryComposer
  menu?: StoryMenu | null
  commands: StoryConversationCommands
}

/** Feature flags cannot expose commands a consumer does not implement. */
export function storyMessageFeatures(commands: StoryConversationCommands, features: ChatMessageFeatures = {}): ChatMessageFeatures {
  return {
    ...features,
    edit: Boolean(commands.edit) && features.edit !== false,
    delete: Boolean(commands.delete) && features.delete !== false,
    fork: Boolean(commands.fork) && features.fork !== false,
    reroll: Boolean(commands.reroll) && features.reroll !== false,
    resend: Boolean(commands.resend) && features.resend !== false,
    alternatives: Boolean(commands.selectVariant) && features.alternatives !== false,
    directorNotes: Boolean(commands.updateDirectorNote && commands.deleteDirectorNote) && features.directorNotes !== false,
  }
}
